/* Angular imports */
import { Component, OnInit, Inject, ElementRef, ViewChild, HostListener, AfterViewInit } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

/* Supporting services */
import { OAuthService, AuthConfig, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import { AppOAuthStorage } from '@headpower/angular-oauth2-oidc-extensions';

/* Application specific */
import { environment } from '../environments/environment';
import { AppConfig } from './app.config';

/* HeadPower */
import { BlahService } from '@headpower/blah-ng';
import { MenuItem, CurrentUser, LiveMessagingService, SmoothnessEnforcer, AutoRelieveMode, ToasterService, ToastDuration, ApplicationInsightsService } from '@headpower/layout';
import { FeatureFlagService } from '@headpower/featureflags';
import { IwmLocaleService } from './services/iwm-locale.service';

import { ContentLayoutService, FloatingActionButtonService } from '@headpower/components';
import { UserInterfaceService } from './services/user-interface.service';
import { forkJoin, Subject, mergeMap, map, filter, firstValueFrom } from 'rxjs';
import { ProductRoleService } from './services/product-role.service';

import { CompanySettingsService } from './services/company-settings.service';
import { IHighlightEntity, MapPinItem } from './map/map/map.component';
import { LocalStorage } from './shared/functions/LocalStorage';
import { SessionStorage } from './shared/functions/SessionStorage';
import { HybridUiMapService } from './hybrid-ui-map.service';
import { ProductService } from './portal/product/portal-product.service';
import { ExternalRequestLiveMessage } from './shared/external-request/external-request.model';
import { UserService } from './portal/user/portal-user.service';
import { FloatingChatService } from './services/floating-chat.service';
import { MatIconRegistry } from '@angular/material/icon';
import { SettingsService } from './settings/components/settings.service';
import { SplitAreaService, SplitAreaState } from './split-area.service';
import { FavoritesService } from './shared/favorite-star/favorites.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {

    // Page name changes with the active page
    public applicationPage: string = 'IWM';

    public productId: number = this.config.productId;
    public deputyFeatureProductId: number = this.config.deputyFeatureProductId;
    public accountBaseUri: string;
    public portalBaseUri: string;
    public backendAccountUri: string;
    public backendPortalUri: string;
    public currentUser: CurrentUser = new CurrentUser();
    public locationUri: string;
    public claims: any;
    public appVersion: string = environment.version;

    public menuItems: MenuItem[] = [];
    public environmentVariables: any;
    public productionMode: boolean;

    // Search specific properties
    public searchActive: boolean = false;
    public searchId: string = '';

    public aiChatFeatureEnabled: boolean = false;

    // Is application running on a handset
    public mobile: boolean = false;

    public userHasIpRoleRights = false;

    public get isLoggedIn(): boolean {
        return this._isLoggedIn;
    }
    private _isLoggedIn: boolean = false;

    public get openedFromIwd(): boolean {
        return this._openedFromIwd;
    }
    private _openedFromIwd: boolean = false;

    public splitAreaState = SplitAreaState.None;

    // Map items
    public mapVisibleInRoute = false;
    public showMap: boolean;
    public mapItems: MapPinItem[] = [];
    public highlightItem: IHighlightEntity = undefined;
    @LocalStorage
    public mapSplitWidth: number;
    public mapResized: Subject<void> = new Subject();

    // IWI items
    public iwiUrl: any = 'https://ohjeistot.headpower.fi';
    public iwiVisibleInRoute = false;
    public showIwi = false;
    public ff_iwiSplitView = false;
    @LocalStorage
    public iwiSplitWidth: number;

    // Split ui map
    @SessionStorage
    public mapHeight: number;
    @SessionStorage
    public mapWidth: number;
    @ViewChild('mapSplitDiv', { static: false }) mapSplitDiv: ElementRef;

    // Split ui iwi
    @LocalStorage
    public iwiHeight: number;
    @SessionStorage
    public iwiWidth: number;
    @ViewChild('iwiSplitDiv', { static: false }) iwiSplitDiv: ElementRef;

    public get splitAreaSize(): number {
        return this.showMap ? (100 - this.mapSplitWidth) : (100 - this.iwiSplitWidth);
    }

    @HostListener('document:keydown.shift.control.k', ['$event'])
    openDialog(_e: KeyboardEvent) {
        this.splitAreaService.toggleMapVisibility();
    }

    @ViewChild('openWithIdField') openWithIdField: ElementRef;

    constructor(
        private oAuthService: OAuthService,
        private oAuthStorage: AppOAuthStorage,
        private roleService: ProductRoleService,
        private productService: ProductService,
        private companySettingsService: CompanySettingsService,
        private readonly settingService: SettingsService,
        private userService: UserService,
        private router: Router,
        private route: ActivatedRoute,
        private titleService: Title,
        private appInsightsService: ApplicationInsightsService,
        private config: AppConfig,
        private featureFlagService: FeatureFlagService,
        private contentLayoutService: ContentLayoutService,
        private fabService: FloatingActionButtonService,
        private userInterfaceService: UserInterfaceService,
        private iwmLocaleService: IwmLocaleService,
        private liveMessagingService: LiveMessagingService,
        private blahService: BlahService,
        private toaster: ToasterService,
        private smoothness: SmoothnessEnforcer,
        private uiMapService: HybridUiMapService,
        // This is used in template
        private readonly floatingChatService: FloatingChatService,
        private readonly iconRegistry: MatIconRegistry,
        private readonly splitAreaService: SplitAreaService,
        @Inject(DOCUMENT) private document: Document,
        private favoritesService: FavoritesService,
    ) {
        this.initBaseProperties();

        this.initAuthentication();

        // Initialize Application Insights
        this.appInsightsService.init(this.config.applicationInsightsConnectionString, true)

        // Observe device screen size and change the layout accordingly
        this.userInterfaceService.mobile$
            .subscribe(state => {
                this.mobile = state;
            });

        this.uiMapService.highlightItem$.subscribe(res => this.highlightItem = res);
        this.uiMapService.items$.subscribe(res => this.mapItems = res);

        // init split map layout
        this.splitAreaService.splitAreaState$.subscribe(res => {
            this.splitAreaState = res;
            this.showMap = res === SplitAreaState.Map;
            this.showIwi = res === SplitAreaState.IWI;
        });
        if (this.mapSplitWidth === null) {
            this.mapSplitWidth = 25;
        }

        if (this.iwiSplitWidth === null) {
            this.iwiSplitWidth = 25;
        }

        // Use material symbols outlined as default for mat-icon, instead of deprecated material icons
        this.iconRegistry.setDefaultFontSetClass('material-symbols-outlined');
    }


    ngOnInit() {
        this.blahService.localeChange$
            .subscribe(() => {
                this.iwmLocaleService.setLocale();
                this.contentLayoutService.refresh();
            });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                map(() => this.route),
                map(route => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter(route => route.outlet === 'primary'),
                mergeMap(route => route.data)
            )
            .subscribe(event => {
                // Reset fab state after routing
                this.fabService.resetState();

                // Check if hybrid map should be hidden based on route
                this.mapVisibleInRoute = !(event['hybridMapHidden'] === true);
                this.iwiVisibleInRoute = !(event['instructionsHidden'] === true);

                // Set window title
                let fullTitle = 'Portal | Headpower';
                this.applicationPage = 'IWM';
                if (event['title'] !== undefined) {
                    // Note: this does not change title on the page user is on when changing language.
                    // Only subsequent routes from then on will have titles in the new selected language.

                    const translatedTitle = this.blahService.blah(event['title']);
                    fullTitle = translatedTitle + ' | ' + fullTitle;
                    this.applicationPage = translatedTitle;
                }
                this.titleService.setTitle(fullTitle);

                // update locationUri (for helpdesk)
                this.locationUri = this.document.location.href;
            });

        this.initIwiSplitView();
    }

    ngAfterViewInit() {
        setInterval(() => this.setMapSize(), 250);

        setInterval(() => this.setIwiSize(), 250);
    }

    private initBaseProperties() {
        this.environmentVariables = {
            environment: environment,
            config: this.config.getAll()
        };

        this.accountBaseUri = this.config.accountBaseUri;
        this.portalBaseUri = this.config.portalBaseUri;
        this.backendAccountUri = this.config.backendAccountUri;
        this.backendPortalUri = this.config.backendPortalUri;

        this.productionMode = environment.production;
    }

    public logOut() {
        this.oAuthStorage.clear();

        this.oAuthService.logOut();
    }

    private createAuthConfig() {
        const baseUri = document.baseURI.replace(/\/$/, ''); // Remove possible final slash
        const authConfig = new AuthConfig();

        // Identity provider's uri
        authConfig.issuer = this.config.accountBaseUri + 'identity';

        // Set flow to authorization code
        authConfig.responseType = 'code';

        // Client secret, required by auth endpoint
        authConfig.dummyClientSecret = this.config.clientSecret;

        // Auth redirect (callback) uri
        authConfig.redirectUri = baseUri + '/auth/callback';

        // Client id
        authConfig.clientId = 'headpower_web_app';

        // Scopes used by the app
        authConfig.scope = 'openid profile email offline_access headpower:api headpower:app roles';

        // Activate session checks to get event if session has terminated,
        // f.ex. user has logged out from another app.
        authConfig.sessionChecksEnabled = this.config.environmentName !== 'LOCAL';
        authConfig.sessionCheckIntervall = 10000; // 10 seconds

        // Set tolerance for the token expiration time according to auth server
        authConfig.clockSkewInSec = 5 * 60; // 5 minutes

        // FYI: Debug should be enabled only when debugging login flow
        authConfig.showDebugInformation = false;

        return authConfig;
    }

    private initAuthentication() {
        this.oAuthService.configure(this.createAuthConfig());
        this.oAuthService.tokenValidationHandler = new JwksValidationHandler();

        // Uses refresh token so offline_access claim is mandatory
        this.oAuthService.setupAutomaticSilentRefresh();

        // Load discovery document. This will also start the login process.
        this.oAuthService.loadDiscoveryDocument();

        this.oAuthService.events
            .subscribe(event => {
                switch (event.type) {
                    case 'token_received':
                        // Token is received when user freshly authenticates and when token is silently refreshed.
                        // Load/refresh user profile after token is received.
                        this.oAuthService.loadUserProfile();
                        break;

                    case 'user_profile_loaded':
                        this.handlePostTokenReceive();

                        // Call login handlers only in login flow
                        if (!this.isLoggedIn) {
                            this.oAuthStorage.setAppItem('logged_in', 'true');

                            this.handleLoginSideEffects();
                            this.handlePostLogin(true);
                        }
                        break;

                    case 'session_changed':
                        console.log(`Authentication ${event.type.replace('_', ' ')}`);
                        break;

                    case 'session_terminated':
                        console.log(`Authentication ${event.type.replace('_', ' ')}, logging out...`);
                        this.logOut();
                        break;
                }

                // Catch any error event and log them to AI
                if (event instanceof OAuthErrorEvent) {
                    console.error('[Auth]', event);

                    this.appInsightsService.trackException(
                        new Error(`${event.type} (${(event.reason as any)?.error?.error || '-'})`));
                }
            });

        // Check if user has authenticated before
        if (this.oAuthService.hasValidIdToken()) {
            this.handlePostTokenReceive();

            const wasLoggedIn = !!this.oAuthStorage.getAppItem('logged_in');

            // Check if user has authenticated in another app
            if (!wasLoggedIn) {
                this.oAuthStorage.setAppItem('logged_in', 'true');

                this.handleLoginSideEffects();
            }

            this.handlePostLogin(false);
        }
    }

    // FYI: This method is called asynchronously or synchronously after user has authenticated (freshly or before)
    //      and profile is loaded (login flow is in progress).
    //      Called also every time when token is silently refreshed.
    private handlePostTokenReceive() {
        // Set claims
        this.setClaimProperties();
    }

    // FYI: This method is called asynchronously or synchronously after user has authenticated (freshly or before).
    //      Meant for ex. data collection, previous session data removal etc.
    //      Called only once per app session.
    private handleLoginSideEffects() {
        // Remove possible old session data
        [
            '_querySettingsWorksite', '_querySettings', '_querySettingsInvoicingProposal',
            '_planlistSettings', 'invoicingProposalListSettings', 'tenderingListSettings',
            '_querySettingsBidderList', '_querySettingsTendererList', '_savedSort',
            '_savedPage', '_savedWorksiteId', '_showArchivedTasks', '_userCanSeeAllTasks',
            'itemsCache', 'highlightedItemCache', 'showMapCache'
        ]
            .forEach(k => sessionStorage.removeItem(k));

        // Send product usage statistics
        const redirectUrl = this.oAuthStorage.getAppItem('post_login_redirect_url') || '/';

        this.productService.addUsageStatistics(redirectUrl)
            .subscribe();
    }

    // FYI: This method is called once in two possible ways:
    //      1. Asynchronously (when user has freshly authenticated).
    //      2. Synchronously from constructor (when user has authenticated before in current session).
    //
    //      To comply with both cases ensure that:
    //        - synchronous initializers (constructor, ngOnInit etc.) wont access properties which are not defined until in this method.
    //        - every property this method will access is defined before initAuthenticaton call, e.g. menuItems.
    private handlePostLogin(freshLogin: boolean) {
        this._isLoggedIn = true;

        // Init services
        this.initFeatureFlagsService();
        this.initSmoothness();
        this.initRoleService();
        this.initRequireService();
        this.initLiveMessagingService();

        // Add menu items
        this.handleMenuItems(freshLogin);

        // Add page loaded class for robot tests
        setTimeout(() => document.body.classList.add('page-loaded'), 250);

        // Check if AI Chat feature is available
        this.featureFlagService.isFeatureAvailable('IWM.AIChat').subscribe(o => {
            this.aiChatFeatureEnabled = o;
        });

        // Check if Favorites feature is available
        this.featureFlagService.isFeatureAvailable('IWM.Favorites').subscribe(o => {
            this.favoritesService.isFavoritesAvailable = o;
        });
    }

    private initFeatureFlagsService() {
        const flagClaims = {
            'env': this.config.environmentName,
            ...this.claims
        };

        // Configure FeatureFlags
        this.featureFlagService.config = {
            environment: this.environmentVariables,
            claims: flagClaims,
            baseUri: 'https://backend.headpower.fi',
            filterClaims: [
                'iss', 'aud', 'exp', 'nbf', 'nonce', 'iat',
                'at_hash', 'sid', 'auth_time', 'idp', 'amr'
            ]
        };
        this.featureFlagService.init();
    }

    private initSmoothness() {
        // Smoothness Enforcer is enabled only in production mode
        this.smoothness.production = environment.production;

        if (!environment.production) {
            console.log('[IWM.SmoothnessEnforcer]', 'Smoothness Enforcer is disabled because app is in development mode.');
            return;
        }

        if (!this.smoothness.armed) {
            this.smoothness.arm({
                appId: 'iwm',
                impactTreshold: 100,
                impactOnNavigation: 2,
                impactOnIdle: 1,
                autoRelieve: AutoRelieveMode.OnNavigation
            });

            console.log('[IWM.SmoothnessEnforcer]', 'Smoothness Enforcer is now armed.');
        }
    }

    private initRoleService() {
        this.roleService.config = {
            environment: this.environmentVariables,
            debug: false,
            baseUri: this.config.backendPortalUsersUri,
            productId: this.config.productId.toString()
        };
        this.roleService.init();
    }

    private initRequireService() {
        this.companySettingsService.loadAll();
    }

    private initLiveMessagingService() {
        this.featureFlagService.isFeatureAvailable('IWM.LiveMessaging')
            .subscribe(result => {
                if (result) {
                    if (!this.liveMessagingService.initialized && !this.liveMessagingService.initializing) {
                        const logLevel = this.productionMode ?
                            2 : // Information in prod mode
                            1;  // Debug in dev mode

                        this.liveMessagingService.init(
                            this.config.productId,
                            `${this.config.backendMessageUri}LiveMessaging.WebAPI/`,
                            () => this.oAuthService.getAccessToken(),
                            logLevel
                        );
                    }
                    this.listenProductWideLiveMessages();
                } else {
                    console.log('[IWM.LiveMessaging]', 'Live messaging disabled by feature flag.');
                }
            });
    }

    private initIwiSplitView() {
        this.featureFlagService.isFeatureAvailable('IWM.SplitViewForIWIApp')
            .subscribe(res => {
                if (res) {
                    this.ff_iwiSplitView = res;
                }
            });
    }

    private listenProductWideLiveMessages() {
        // External request finished note
        this.liveMessagingService.listenToSingleMessages('iwm/externalRequest/**')
            .subscribe(result => {
                const payload = result.Payload as ExternalRequestLiveMessage;

                let message: string;
                let messageParams: any[];

                switch (payload.RequestState) {
                    case 'Completed':
                        payload.RequestType === 'ExternalUnitImport' && (message = 'default.externalUnitImport.importUnitsRequestCompleted');
                        payload.RequestType === 'ExternalAreaImport' && (message = 'default.externalAreaImport.importAreasRequestCompleted');
                        break;

                    case 'Failed':
                        payload.RequestType === 'ExternalUnitImport' && (message = 'default.externalUnitImport.importUnitsRequestFailed');
                        payload.RequestType === 'ExternalAreaImport' && (message = 'default.externalAreaImport.importAreasRequestFailed');

                        messageParams = [payload.RequestStatusCode || '-'];
                        break;
                }

                if (message) {
                    const translatedMessage = this.blahService.blah({
                        key: message,
                        params: messageParams
                    });

                    this.toaster.create(translatedMessage, {
                        actionLabel: 'OK',
                        duration: ToastDuration.extra,
                        color: payload.RequestState === 'Completed' ? 'success' : 'warn'
                    });
                }
            });
    }

    private async handleMenuItems(freshLogin: boolean) {
        // User has freshly authenticated and will be redirected
        if (freshLogin) {
            const redirectUrl = new URL(
                this.oAuthStorage.getAppItem('post_login_redirect_url') || '/',
                window.location.origin);

            this._openedFromIwd = redirectUrl.searchParams.has('openedFromIwd');
        }
        // User is already authenticated in current session and no redirect was made
        else {
            // If called synchronously from constructor, routes are not yet initialized
            // so await for navigation to end before continuing
            if (!this.route.component) {
                await firstValueFrom(
                    this.router.events
                        .pipe(
                            filter(event => event instanceof NavigationEnd)
                        ));
            }

            this._openedFromIwd = this.route.snapshot.queryParamMap.has('openedFromIwd');
        }

        if (this.openedFromIwd) {
            // If opened from IWD set it's menu items
            this.setIwdMenuItems();
        } else {
            // Otherwise set app menu items
            this.setAppMenuItems();
        }
    }

    private setAppMenuItems() {
        forkJoin({
            ipAccess: this.roleService.userHasRole('IwmAllowedToAccessInvoicingProposals'),
            ipAsHandlerAccess: this.roleService.userHasRole('IwmAllowedToAccessInvoicingProposalsWhereHandler'),
            iwmAdmin: this.roleService.userHasRole('IwmContentAdministrator'),
            tenderingAccess: this.roleService.userHasRole('IwmAllowedToAccessTenderingsAndBids'),
            taxReportingProductAccess: this.productService.companyHasLicenseToProduct(this.config.iwmTaxReportingId),
            taxReportingRoleAccess: this.roleService.userHasRole('IwmAllowedToAccessTaxAuthorityReporting'),
            iwmInventoryProductAccess: this.productService.companyHasLicenseToInventory(),
            satNetMonitorAccess: this.productService.companyHasLicenseToProduct(this.config.iwmSatNetProductId),
            satNetMonitorFlag: this.featureFlagService.isFeatureAvailable('SatNet.NewMap'),
            hiddenMenus: this.settingService.getHiddenMenus()
        })
            .subscribe(result => {
                let items = [];

                if (-1 === result.hiddenMenus.value.findIndex(t => t.TargetType === 'Plan')) {
                    items.push(new MenuItem('plan.plans', '/Plans', {
                        icon: 'view_headline',
                        translate: true,
                        backgroundColor: 'var(--color-primary-800)',
                        textColor: 'var(--color-primary-800-contrast)'
                    }));
                }
                if (-1 === result.hiddenMenus.value.findIndex(t => t.TargetType === 'Worksite')) {
                    items.push(new MenuItem('worksite.worksites', '/Worksites', {
                        icon: 'view_headline',
                        translate: true,
                        backgroundColor: 'var(--color-primary-600)',
                        textColor: 'var(--color-primary-600-contrast)'
                    }));
                }
                items.push(new MenuItem('task.tasklist.tasklist', '/Tasks', {
                    icon: 'view_headline',
                    translate: true,
                    backgroundColor: 'var(--color-primary-400)',
                    textColor: 'var(--color-primary-400-contrast)'
                }));

                if ((result.ipAccess || result.ipAsHandlerAccess) &&
                    -1 === result.hiddenMenus.value.findIndex(t => t.TargetType === 'InvoicingProposal')) {
                    this.userHasIpRoleRights = true;
                    items.splice(3, 0, new MenuItem('invoicingProposal.invoicingProposals', '/InvoicingProposals', {
                        icon: 'view_headline',
                        translate: true,
                        backgroundColor: 'var(--color-primary-100)',
                        textColor: 'var(--color-primary-100-contrast)'
                    }));
                }

                if (-1 === result.hiddenMenus.value.findIndex(t => t.TargetType === 'Dashboard')) {
                    items.push(new MenuItem('dashboard.dashboard', 'Tasks/Dashboard', {
                        icon: 'dashboard',
                        translate: true,
                        textColor: 'var(--color-primary-500)'
                    }));
                }

                if (result.satNetMonitorAccess && result.satNetMonitorFlag) {
                    items.push(new MenuItem('SatnetMonitor', '/SatnetMonitor', { icon: 'satellite_alt', translate: false }));
                }

                // Hides product extensions unless have access to tax reporting or to inventory
                // Change if new elements added to product extensions page
                if (result.taxReportingProductAccess && result.taxReportingRoleAccess || result.iwmInventoryProductAccess['value'] !== 'None') {
                    items.push(new MenuItem('settings.categories.productExtensions', '/ProductExtensions', { icon: 'widgets', translate: true }));
                }

                items.push(new MenuItem('settings.administration', '/Settings/Menu', { icon: 'settings', translate: true }));

                if (result.tenderingAccess && -1 === result.hiddenMenus.value.findIndex(t => t.TargetType === 'Tendering')) {
                    items.splice(0, 0, new MenuItem('tendering.tenderings', '/Tenderings', {
                        icon: 'view_headline',
                        translate: true,
                        backgroundColor: 'var(--color-primary-900)',
                        textColor: 'var(--color-primary-900-contrast)'
                    }));
                }
                // This is checked here so that item get added as last item after other role based checks
                if (this.userService.userHasRoleClaim('HeadPowerAdmin')) {
                    items.push(new MenuItem('admin.headpowerCustomerAdministration', '/HeadPowerSettings', { icon: 'admin_panel_settings', translate: true }));
                }

                this.menuItems = items;
            });
    }

    /**
     * Assigning menu items when worksite is accessed from IWD
     */
    private setIwdMenuItems() {
        const recordListUrl = `${this.config.portalBaseUri}${this.config.iwdPortalEndpoint}Record-list`;
        const defectListUrl = `${this.config.portalBaseUri}${this.config.iwdPortalEndpoint}Defect-list`;
        const dashboardUrl = `${this.config.portalBaseUri}${this.config.iwdPortalEndpoint}Dashboard`;
        const settingsUrl = `${this.config.portalBaseUri}${this.config.iwdPortalEndpoint}Settings`;

        let items = [];

        items.push(new MenuItem('worksite.worksites', '/Worksites', {
            icon: 'view_headline',
            translate: true,
            backgroundColor: 'var(--color-primary-600)',
            textColor: 'var(--color-primary-600-contrast)'
        }));
        items.push(new MenuItem('default.records', recordListUrl, { icon: 'view_headline', translate: true }));
        items.push(new MenuItem('default.defectsAndNotes', defectListUrl, { icon: 'view_headline', translate: true }));
        items.push(new MenuItem('dashboard.dashboard', dashboardUrl, { icon: 'dashboard', translate: true }));
        items.push(new MenuItem('settings.administration', settingsUrl, { icon: 'settings', translate: true }));

        this.menuItems = items;
    }

    /**
     * Set user properties from claims
     */
    private setClaimProperties() {
        const claims: any = this.oAuthService.getIdentityClaims();
        this.claims = claims;

        this.currentUser.userId = claims['sub'];
        this.currentUser.givenName = claims['given_name'];
        this.currentUser.familyName = claims['family_name'];
        this.currentUser.email = claims['email'];
        this.currentUser.picture = claims['picture'];
        this.currentUser.accessToken = this.oAuthService.getAccessToken();
        this.currentUser.companyName = claims['headpower:company_name'];

        // Set locale only in login flow
        if (!this.isLoggedIn) {
            this.setBlahLanguage();
            this.iwmLocaleService.setLocale();
        }
    }

    /**
     * Search for tasks, worksites and invoicing proposals by iwm id
     */
    public searchWithIwmId() {
        if (this.searchId) {
            this.router.navigate(['OpenTarget', this.searchId]);
            setTimeout(() => {
                this.searchId = '';
                this.openWithIdField.nativeElement.blur();
            }, 0)
        }
    }

    public sidenavToggleChanged(opened: boolean): void {
        if (this.mobile) {
            const showFabBtn = !opened;
            this.fabService.setVisibility(showFabBtn);
        }
    }

    /**
    * Sets the blah language based on locale claim.
    */
    private setBlahLanguage() {
        const claimLocale = this.claims['locale'] as string;

        if (claimLocale) {
            const separatorIndex = claimLocale.indexOf('-');
            const localeLanguage = separatorIndex > -1 ? claimLocale.substring(0, separatorIndex) : claimLocale;
            const locale = this.blahService.availableLocales.find(l => l.startsWith(localeLanguage)) ?? 'fi-FI';

            this.blahService.changeLocale(locale);
        }
    }

    /**
    * Set sizes for split map area
    */
    private setMapSize() {
        if (this.mapSplitDiv === undefined) {
            return;
        }

        const newWidth = this.mapSplitDiv.nativeElement.clientWidth;
        const newHeight = this.mapSplitDiv.nativeElement.clientHeight;

        if (this.mapHeight !== newHeight || this.mapWidth !== newWidth) {
            this.mapWidth = newWidth;
            this.mapHeight = newHeight;
            // Using setTimeout allows new size to be detected correctly when
            // maximising/restoring window.
            setTimeout(() => { this.mapResized.next(); }, 100);
        }
    }

    /**
     * Set sizes for split Iwi area
     */
    private setIwiSize() {
        if (this.iwiSplitDiv === undefined) {
            return;
        }

        const newWidth = this.iwiSplitDiv.nativeElement.clientWidth;
        const newHeight = this.iwiSplitDiv.nativeElement.clientHeight;

        if (this.iwiHeight !== newHeight || this.iwiWidth !== newWidth) {
            this.iwiWidth = newWidth;
            this.iwiHeight = newHeight;
        }
    }

    public splitDragEnd({ sizes }) {

        if (this.splitAreaState === SplitAreaState.Map) {
            this.mapSplitWidth = sizes[0];
            this.setMapSize();
        }

        if (this.splitAreaState === SplitAreaState.IWI) {
            this.iwiSplitWidth = sizes[0];
            this.setIwiSize();
        }
    }
}
